import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Calendar, Users, Phone, Mail, Clock, LogOut, Activity, UserPlus, MessageSquare, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function getAuthHeaders() {
  const token = sessionStorage.getItem('admin_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
  new: 'bg-indigo-100 text-indigo-700',
  contacted: 'bg-sky-100 text-sky-700',
  converted: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-gray-100 text-gray-500',
};

function StatusBadge({ status }) {
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`} data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
}

function StatusDropdown({ currentStatus, options, onSelect, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs text-[#0066b3] hover:text-[#004a82] font-medium" data-testid={`status-dropdown-${id}`}>
        Update <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-6 z-20 bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[120px]">
          {options.filter(o => o !== currentStatus).map(opt => (
            <button key={opt} onClick={() => { onSelect(opt); setOpen(false); }} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 capitalize">
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4 shadow-sm" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={22} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#0f172a]">{value}</p>
        <p className="text-xs text-[#64748b]">{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('appointments');
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, apptsRes, leadsRes, contactsRes] = await Promise.all([
        fetch(`${API}/api/admin/stats`, { headers, credentials: 'include' }),
        fetch(`${API}/api/admin/appointments`, { headers, credentials: 'include' }),
        fetch(`${API}/api/admin/leads`, { headers, credentials: 'include' }),
        fetch(`${API}/api/admin/contacts`, { headers, credentials: 'include' }),
      ]);

      if (statsRes.status === 401) {
        sessionStorage.removeItem('admin_token');
        sessionStorage.removeItem('admin_user');
        navigate('/admin/login');
        return;
      }

      setStats(await statsRes.json());
      setAppointments(await apptsRes.json());
      setLeads(await leadsRes.json());
      setContacts(await contactsRes.json());
    } catch {
      toast.error('Failed to load data');
    }
  }, [navigate]);

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchData();
  }, [navigate, fetchData]);

  const updateAppointmentStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/admin/appointments/${id}`, {
        method: 'PATCH', headers: getAuthHeaders(), credentials: 'include',
        body: JSON.stringify({ status })
      });
      if (res.ok) { toast.success(`Appointment ${status}`); fetchData(); }
    } catch { toast.error('Update failed'); }
  };

  const updateLeadStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/admin/leads/${id}`, {
        method: 'PATCH', headers: getAuthHeaders(), credentials: 'include',
        body: JSON.stringify({ status })
      });
      if (res.ok) { toast.success(`Lead ${status}`); fetchData(); }
    } catch { toast.error('Update failed'); }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  const filteredAppointments = useMemo(() => filterStatus ? appointments.filter(a => a.status === filterStatus) : appointments, [appointments, filterStatus]);
  const filteredLeads = useMemo(() => filterStatus ? leads.filter(l => l.status === filterStatus) : leads, [leads, filterStatus]);

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
  };

  if (!stats) {
    return <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0066b3] border-t-transparent"></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#0066b3] rounded-lg flex items-center justify-center">
            <Activity size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#0f172a]">Sakthi Hospital Admin</h1>
            <p className="text-xs text-[#64748b]">Management Dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#64748b]">{adminUser.email}</span>
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={Calendar} label="Total Appointments" value={stats.total_appointments} color="bg-[#0066b3]" />
          <StatCard icon={Clock} label="Pending" value={stats.pending_appointments} color="bg-amber-500" />
          <StatCard icon={Users} label="Confirmed" value={stats.confirmed_appointments} color="bg-emerald-500" />
          <StatCard icon={UserPlus} label="Total Leads" value={stats.total_leads} color="bg-indigo-500" />
          <StatCard icon={Activity} label="New Leads" value={stats.new_leads} color="bg-sky-500" />
          <StatCard icon={MessageSquare} label="Contacts" value={stats.total_contacts} color="bg-violet-500" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 bg-white rounded-xl border border-gray-100 p-1 w-fit">
          {[
            { key: 'appointments', label: 'Appointments', count: appointments.length },
            { key: 'leads', label: 'Leads', count: leads.length },
            { key: 'contacts', label: 'Contacts', count: contacts.length },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setFilterStatus(''); }}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.key ? 'bg-[#0066b3] text-white shadow-sm' : 'text-[#64748b] hover:text-[#0f172a]'}`}
            >
              {t.label} <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${tab === t.key ? 'bg-white/20' : 'bg-gray-100'}`}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {tab === 'appointments' && (
            <>
              <div className="px-5 py-3 border-b border-gray-50 flex items-center gap-2">
                <span className="text-xs text-[#64748b]">Filter:</span>
                {['', 'pending', 'confirmed', 'completed', 'cancelled'].map(s => (
                  <button key={s} onClick={() => setFilterStatus(s)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${filterStatus === s ? 'bg-[#0066b3] text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
                    {s || 'All'}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="appointments-table">
                  <thead>
                    <tr className="bg-[#f8fafc]">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Patient</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Contact</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Department</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Date</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.length === 0 ? (
                      <tr><td colSpan="6" className="text-center py-12 text-[#94a3b8]">No appointments found</td></tr>
                    ) : filteredAppointments.map((a, i) => (
                      <tr key={a.id || i} className="border-t border-gray-50 hover:bg-[#f8fafc]/50" data-testid={`appointment-row-${i}`}>
                        <td className="px-5 py-3 font-medium text-[#0f172a]">{a.name}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1.5 text-[#64748b]"><Phone size={12} />{a.phone}</div>
                          <div className="flex items-center gap-1.5 text-[#94a3b8] text-xs mt-0.5"><Mail size={10} />{a.email}</div>
                        </td>
                        <td className="px-5 py-3 text-[#64748b]">{a.department}</td>
                        <td className="px-5 py-3 text-[#64748b]">{a.preferred_date} {a.preferred_time}</td>
                        <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                        <td className="px-5 py-3">
                          <StatusDropdown currentStatus={a.status} options={['pending', 'confirmed', 'completed', 'cancelled']} onSelect={(s) => updateAppointmentStatus(a.id, s)} id={a.id} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === 'leads' && (
            <>
              <div className="px-5 py-3 border-b border-gray-50 flex items-center gap-2">
                <span className="text-xs text-[#64748b]">Filter:</span>
                {['', 'new', 'contacted', 'converted', 'lost'].map(s => (
                  <button key={s} onClick={() => setFilterStatus(s)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${filterStatus === s ? 'bg-[#0066b3] text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
                    {s || 'All'}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead>
                    <tr className="bg-[#f8fafc]">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Name</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Contact</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Specialty</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Treatment</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Source</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Date</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.length === 0 ? (
                      <tr><td colSpan="8" className="text-center py-12 text-[#94a3b8]">No leads found</td></tr>
                    ) : filteredLeads.map((l, i) => (
                      <tr key={l.id || i} className="border-t border-gray-50 hover:bg-[#f8fafc]/50" data-testid={`lead-row-${i}`}>
                        <td className="px-5 py-3 font-medium text-[#0f172a]">{l.name}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1.5 text-[#64748b]"><Phone size={12} />{l.phone}</div>
                          <div className="flex items-center gap-1.5 text-[#94a3b8] text-xs mt-0.5"><Mail size={10} />{l.email}</div>
                        </td>
                        <td className="px-5 py-3 text-[#64748b]">{l.specialty}</td>
                        <td className="px-5 py-3 text-[#64748b]">{l.treatment}</td>
                        <td className="px-5 py-3 text-[#64748b] text-xs">{l.source_page}</td>
                        <td className="px-5 py-3 text-[#64748b]">{formatDate(l.created_at)}</td>
                        <td className="px-5 py-3"><StatusBadge status={l.status} /></td>
                        <td className="px-5 py-3">
                          <StatusDropdown currentStatus={l.status} options={['new', 'contacted', 'converted', 'lost']} onSelect={(s) => updateLeadStatus(l.id, s)} id={l.id} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === 'contacts' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="contacts-table">
                <thead>
                  <tr className="bg-[#f8fafc]">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Name</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Email</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Phone</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Message</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748b] uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-12 text-[#94a3b8]">No contacts found</td></tr>
                  ) : contacts.map((c, i) => (
                    <tr key={c.id || i} className="border-t border-gray-50 hover:bg-[#f8fafc]/50" data-testid={`contact-row-${i}`}>
                      <td className="px-5 py-3 font-medium text-[#0f172a]">{c.name}</td>
                      <td className="px-5 py-3 text-[#64748b]">{c.email}</td>
                      <td className="px-5 py-3 text-[#64748b]">{c.phone}</td>
                      <td className="px-5 py-3 text-[#64748b] max-w-xs truncate">{c.message}</td>
                      <td className="px-5 py-3 text-[#64748b]">{formatDate(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
